import type { CSSProperties } from "react";
import type { SpectralState } from "../data/types";
import { STATE_COLORS, stateColor } from "../data/types";

interface Props {
  stacked: boolean;
  /** Whether the loaded object has discrete spectral states at all. */
  hasStates: boolean;
  primaryState: SpectralState | null;
  comparisonState: SpectralState | null;
  hasComparison: boolean;
  showReddened: boolean;
  showFeatures: boolean;
}

function Swatch({ color, style }: { color: string; style?: CSSProperties }) {
  return <span className="swatch" style={{ background: color, ...style }} />;
}

const CONTINUUM_SWATCH: CSSProperties = { height: 2, borderRadius: 0, opacity: 0.6 };

export function PlotLegend(props: Props) {
  const { stacked, hasStates, primaryState, comparisonState, hasComparison, showReddened, showFeatures } = props;

  if (stacked && !hasStates) {
    return (
      <div className="legend">
        <span>
          <Swatch color={stateColor(null)} />
          epochs (no discrete states)
        </span>
      </div>
    );
  }
  if (stacked) {
    return (
      <div className="legend">
        {(["Bright", "Intermediate", "Dim"] as const).map((state) => (
          <span key={state}>
            <Swatch color={STATE_COLORS[state]} />
            {state}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="legend">
      <span>
        <Swatch color={stateColor(primaryState)} />
        primary
      </span>
      {hasComparison ? (
        <span>
          <Swatch color={stateColor(comparisonState)} />
          comparison
        </span>
      ) : null}
      {showReddened ? (
        <span>
          <Swatch color="var(--reddened-color)" />
          reddened template
        </span>
      ) : null}
      {showFeatures ? (
        <>
          <span>
            <span className="swatch-feature" style={{ color: "var(--feature-known)" }} />
            detected peak · known line
          </span>
          <span>
            <span className="swatch-feature" style={{ color: "var(--feature-discovery)" }} />
            detected peak · unidentified
          </span>
          <span>
            <Swatch color="var(--canvas-axis-text)" style={CONTINUUM_SWATCH} />
            estimated continuum
          </span>
        </>
      ) : null}
    </div>
  );
}
